import * as THREE from 'three';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * MASTER 3D RENDER LOOP & SCROLL CHOREOGRAPHY
 */

export function initRenderLoop({
  scene,
  cameraController,
  renderer,
  airframe,
  explodedController,
  morphController,
  materials,
  onTelemetryUpdate
}) {
  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.innerWidth < 768;

  let isTabVisible = true;
  let isWireframeOnly = false;
  let currentRpm = 0;
  let targetRpm = 4200;
  let flightMode = "HOVER";
  let currentAlt = 0.0;
  let isLanded = false;
  let scrollProgress = 0.0;

  // Visibility change pause
  document.addEventListener('visibilitychange', () => {
    isTabVisible = document.visibilityState === 'visible';
  });

  // Turntable base rotation
  let turntableSpeed = isReducedMotion ? 0 : 0.003;

  // Set wireframe mode
  function toggleWireframeMode() {
    isWireframeOnly = !isWireframeOnly;
    scene.traverse(child => {
      if (child.isMesh) {
        child.visible = !isWireframeOnly;
      }
    });
    return isWireframeOnly ? "WIREFRAME" : "SOLID + EDGES";
  }

  // Scroll Choreography Timeline using GSAP ScrollTrigger
  const masterTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: "body",
      start: "top top",
      end: "bottom bottom",
      scrub: 1.2,
      onUpdate: self => {
        const progress = self.progress;
        // Calculate deterministic altitude based on scroll
        if (progress > 0.88) {
          // Landing descent in Contact section
          currentAlt = Math.max(0, (1.0 - (progress - 0.88) / 0.12) * 4.0);
          if (currentAlt < 0.1) {
            currentAlt = 0.0;
            flightMode = "LANDED";
            isLanded = true;
            targetRpm = 0;
          } else {
            flightMode = "TOUCHDOWN";
            targetRpm = 800;
          }
        } else if (progress > 0.65) {
          flightMode = "PLAN-VIEW";
          currentAlt = 62.0;
          targetRpm = 4200;
          isLanded = false;
        } else if (progress > 0.40) {
          flightMode = "TRANSITION";
          currentAlt = 45.0;
          targetRpm = 4800;
          isLanded = false;
        } else if (progress > 0.15) {
          flightMode = "EXPLODED";
          currentAlt = 12.5;
          targetRpm = 2100;
          isLanded = false;
        } else {
          flightMode = "HOVER";
          currentAlt = Math.min(10.0, progress * 40.0);
          targetRpm = 4200;
          isLanded = false;
        }
      }
    }
  });

  if (!isReducedMotion) {
    // 1. Hero -> About: Camera zooms in, airframe separates into exploded view
    masterTimeline.to(cameraController.camera.position, {
      x: cameraController.cameraStates.about.position.x,
      y: cameraController.cameraStates.about.position.y,
      z: cameraController.cameraStates.about.position.z,
      ease: "power2.inOut",
      duration: 2
    }, 0);

    masterTimeline.to(cameraController.targetLookAt, {
      x: cameraController.cameraStates.about.lookAt.x,
      y: cameraController.cameraStates.about.lookAt.y,
      z: cameraController.cameraStates.about.lookAt.z,
      ease: "power2.inOut",
      duration: 2
    }, 0);

    masterTimeline.to({ ratio: 0 }, {
      ratio: 1,
      duration: 2,
      ease: "power2.inOut",
      onUpdate: function() {
        explodedController.setExplodeRatio(this.targets()[0].ratio);
      }
    }, 0.5);

    // 2. About -> Systems / Skills
    masterTimeline.to(cameraController.camera.position, {
      x: cameraController.cameraStates.systems.position.x,
      y: cameraController.cameraStates.systems.position.y,
      z: cameraController.cameraStates.systems.position.z,
      duration: 2
    }, 2);

    masterTimeline.to(cameraController.targetLookAt, {
      x: cameraController.cameraStates.systems.lookAt.x,
      y: cameraController.cameraStates.systems.lookAt.y,
      z: cameraController.cameraStates.systems.lookAt.z,
      duration: 2
    }, 2);

    // 3. Systems -> Projects: Airframe reassembles and morphs
    masterTimeline.to({ ratio: 1 }, {
      ratio: 0,
      duration: 1.5,
      ease: "power2.inOut",
      onUpdate: function() {
        explodedController.setExplodeRatio(this.targets()[0].ratio);
      }
    }, 4);

    masterTimeline.to(cameraController.camera.position, {
      x: cameraController.cameraStates.projects.position.x,
      y: cameraController.cameraStates.projects.position.y,
      z: cameraController.cameraStates.projects.position.z,
      duration: 2
    }, 4);

    // 4. Projects -> Experience: Camera pulls up into top-down orthographic plan view
    masterTimeline.to(cameraController.camera.position, {
      x: cameraController.cameraStates.experience.position.x,
      y: cameraController.cameraStates.experience.position.y,
      z: cameraController.cameraStates.experience.position.z,
      duration: 2
    }, 6);

    masterTimeline.to(cameraController.targetLookAt, {
      x: 0, y: 0, z: 0,
      duration: 2
    }, 6);

    // 5. Experience -> Contact: Landing pad touchdown
    masterTimeline.to(cameraController.camera.position, {
      x: cameraController.cameraStates.contact.position.x,
      y: cameraController.cameraStates.contact.position.y,
      z: cameraController.cameraStates.contact.position.z,
      duration: 2
    }, 8);

    masterTimeline.to(cameraController.targetLookAt, {
      x: cameraController.cameraStates.contact.lookAt.x,
      y: cameraController.cameraStates.contact.lookAt.y,
      z: cameraController.cameraStates.contact.lookAt.z,
      duration: 2
    }, 8);

    masterTimeline.to(airframe.root.position, {
      y: -2.1,
      duration: 1.5,
      ease: "power2.in"
    }, 8.2);
  }

  // Morph triggers tied to Project section elements
  document.querySelectorAll('[data-morph]').forEach(el => {
    const configName = el.getAttribute('data-morph');
    ScrollTrigger.create({
      trigger: el,
      start: "top center",
      end: "bottom center",
      onEnter: () => morphController.setConfiguration(configName),
      onEnterBack: () => morphController.setConfiguration(configName)
    });
  });

  // Section index rail updater
  const sectionIds = ['hero', 'about', 'systems', 'projects', 'experience', 'education', 'contact'];
  sectionIds.forEach((id, index) => {
    const secEl = document.getElementById(id);
    if (secEl) {
      ScrollTrigger.create({
        trigger: secEl,
        start: "top 60%",
        end: "bottom 60%",
        onEnter: () => {
          document.dispatchEvent(new CustomEvent('sectionchange', { detail: { index, id } }));
        },
        onEnterBack: () => {
          document.dispatchEvent(new CustomEvent('sectionchange', { detail: { index, id } }));
        }
      });
    }
  });

  // Main Render Loop
  let lastTime = performance.now();
  const targetFrameMs = 1000 / (isMobile ? 30 : 60);

  function animate(now) {
    requestAnimationFrame(animate);

    if (!isTabVisible) return;

    const delta = (now - lastTime) / 1000;
    if (delta < targetFrameMs / 1000 * 0.8) return;
    lastTime = now;

    // Slowly spin up or down propeller RPM
    currentRpm += (targetRpm - currentRpm) * 0.04;
    const propSpeed = (currentRpm / 60) * (Math.PI * 2) * 0.016;

    if (!isReducedMotion) {
      // Rotate propellers
      airframe.groups.propellers.forEach(p => {
        p.group.rotation.y += propSpeed * p.direction;
      });

      // Turntable slow rotation in hero section
      if (window.scrollY < window.innerHeight) {
        airframe.root.rotation.y += turntableSpeed;
      }

      // Parallax update
      cameraController.updateParallax(delta);
    }

    // Smooth camera lookAt interpolation
    cameraController.currentLookAt.lerp(cameraController.targetLookAt, 0.08);
    cameraController.camera.lookAt(cameraController.currentLookAt);

    // Update telemetry readout callback
    if (onTelemetryUpdate) {
      onTelemetryUpdate({
        rpm: Math.round(currentRpm),
        mode: flightMode,
        alt: currentAlt.toFixed(1),
        isLanded
      });
    }

    // Airframe is always visible and actively animated on all 3D sections
    airframe.root.visible = true;

    renderer.render(scene, cameraController.camera);
  }

  requestAnimationFrame(animate);

  return {
    toggleWireframeMode,
    setTargetRpm: rpm => { targetRpm = rpm; }
  };
}
